import React, { useState, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import { X, Download, Share2, Trash2, MoreVertical } from 'lucide-react';

/**
 * 카카오톡 스타일 동영상 뷰어
 * Props:
 *   video       - { src, senderName, createdAt, messageId, fileName, isMe }
 *   onClose     - 닫기 콜백
 *   onDownload  - (src, fileName) => void
 *   onDelete    - (messageId) => void (본인 동영상만)
 */
export default function VideoViewer({ video, onClose, onDownload, onDelete }) {
  const [showMore, setShowMore] = useState(false);
  const touchStartRef = useRef(null);
  const videoRef = useRef(null);

  // ESC 키 닫기
  useEffect(() => {
    const handler = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onClose]);

  // 뷰어 열릴 때 body 스크롤 방지
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = ''; };
  }, []);

  const handleDownload = () => {
    if (onDownload) onDownload(video.src, video.fileName);
    setShowMore(false);
  };

  const handleShare = async () => {
    setShowMore(false);
    try {
      if (navigator.share) {
        await navigator.share({ title: video.fileName || '동영상', url: video.src });
      } else {
        handleDownload();
      }
    } catch {}
  };

  const handleDelete = () => {
    setShowMore(false);
    if (onDelete) onDelete(video.messageId);
    onClose();
  };

  // 아래 스와이프로 닫기 (모바일)
  const handleTouchStart = (e) => {
    if (e.touches.length !== 1) return;
    touchStartRef.current = { y: e.touches[0].clientY };
  };

  const handleTouchEnd = (e) => {
    if (!touchStartRef.current) return;
    const dy = (e.changedTouches[0]?.clientY || 0) - touchStartRef.current.y;
    touchStartRef.current = null;
    if (dy > 80) onClose();
  };

  const formatDate = (iso) => {
    if (!iso) return '';
    const d = new Date(iso);
    return `${d.getFullYear()}.${String(d.getMonth() + 1).padStart(2, '0')}.${String(d.getDate()).padStart(2, '0')} ${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
  };

  const viewer = (
    <div
      style={{
        position: 'fixed', inset: 0, zIndex: 99999,
        background: 'rgba(0,0,0,0.96)',
        display: 'flex', flexDirection: 'column',
        userSelect: 'none',
      }}
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
      onClick={() => setShowMore(false)}
    >
      {/* 상단 바 */}
      <div
        style={{
          position: 'absolute', top: 0, left: 0, right: 0,
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '12px 16px',
          background: 'linear-gradient(to bottom, rgba(0,0,0,0.7) 0%, transparent 100%)',
          zIndex: 2,
        }}
        onClick={e => e.stopPropagation()}
      >
        {/* 닫기 */}
        <button
          onClick={onClose}
          style={{ color: '#fff', background: 'none', border: 'none', cursor: 'pointer', padding: 4 }}
        >
          <X size={24} />
        </button>

        {/* 발신자명 + 날짜 */}
        <div style={{ textAlign: 'center', flex: 1, marginLeft: 8 }}>
          <div style={{ color: '#fff', fontSize: 14, fontWeight: 600, lineHeight: 1.3 }}>
            {video.senderName || ''}
          </div>
          <div style={{ color: 'rgba(255,255,255,0.6)', fontSize: 12 }}>
            {formatDate(video.createdAt)}
          </div>
        </div>

        {/* ⋮ 더보기 메뉴 */}
        <div style={{ position: 'relative' }}>
          <button
            onClick={(e) => { e.stopPropagation(); setShowMore(v => !v); }}
            style={{ color: '#fff', background: 'none', border: 'none', cursor: 'pointer', padding: 4 }}
          >
            <MoreVertical size={24} />
          </button>
          {showMore && (
            <div style={{
              position: 'absolute', right: 0, top: '100%', marginTop: 4,
              background: '#222', borderRadius: 8, overflow: 'hidden',
              minWidth: 140, boxShadow: '0 4px 20px rgba(0,0,0,0.5)',
            }}>
              <button
                onClick={(e) => { e.stopPropagation(); handleDownload(); }}
                style={{ width: '100%', display: 'flex', alignItems: 'center', gap: 10, padding: '12px 16px', background: 'none', border: 'none', color: '#fff', fontSize: 14, cursor: 'pointer' }}
              >
                <Download size={16} /> 저장
              </button>
              <button
                onClick={(e) => { e.stopPropagation(); handleShare(); }}
                style={{ width: '100%', display: 'flex', alignItems: 'center', gap: 10, padding: '12px 16px', background: 'none', border: 'none', color: '#fff', fontSize: 14, cursor: 'pointer' }}
              >
                <Share2 size={16} /> 공유
              </button>
              {video.isMe && (
                <button
                  onClick={(e) => { e.stopPropagation(); handleDelete(); }}
                  style={{ width: '100%', display: 'flex', alignItems: 'center', gap: 10, padding: '12px 16px', background: 'none', border: 'none', color: '#ff4d4d', fontSize: 14, cursor: 'pointer' }}
                >
                  <Trash2 size={16} /> 삭제
                </button>
              )}
            </div>
          )}
        </div>
      </div>

      {/* 동영상 영역 */}
      <div
        style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden', padding: '60px 0' }}
        onClick={e => e.stopPropagation()}
      >
        <video
          ref={videoRef}
          src={video.src}
          controls
          autoPlay
          muted
          playsInline
          style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain', borderRadius: 4 }}
          onError={(e) => { e.target.style.display = 'none'; }}
        />
      </div>

      {/* 하단 바 */}
      <div
        style={{
          position: 'absolute', bottom: 0, left: 0, right: 0,
          padding: '16px 24px 32px',
          background: 'linear-gradient(to top, rgba(0,0,0,0.7) 0%, transparent 100%)',
          display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 48,
          zIndex: 2,
        }}
        onClick={e => e.stopPropagation()}
      >
        <button
          onClick={handleDownload}
          style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4, background: 'none', border: 'none', color: '#fff', cursor: 'pointer' }}
        >
          <Download size={22} />
          <span style={{ fontSize: 11 }}>저장</span>
        </button>

        <button
          onClick={handleShare}
          style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4, background: 'none', border: 'none', color: '#fff', cursor: 'pointer' }}
        >
          <Share2 size={22} />
          <span style={{ fontSize: 11 }}>공유</span>
        </button>
      </div>
    </div>
  );

  return ReactDOM.createPortal(viewer, document.body);
}
